import { computed, ref } from 'vue'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { useCustomerStore } from './customerStore'  
import { useListStore } from './listStore'  
import { useProductStore } from './productStore'  
import { updateWithPromoPricing } from './product-fns'
import { HotDeal, IRogue } from '@/js/services/RogueTypes'
import { deepEqual } from '@/js/services/Utils'

type PromoState = {
  lastUpdate: number,
  activePromos: { [productId: number]: HotDeal },
  hundoProductIds: string[],
  customerPrices: { [productId: number]: number }
}

export const usePromoStore = defineStore('promos', () => {
  const activePromos = ref<PromoState['activePromos']>({})
  const hundoProductIds = ref<PromoState['hundoProductIds']>([])
  const customerPrices = ref<PromoState['customerPrices']>({})
  const lastUpdate = ref<PromoState['lastUpdate']>(0)
  // Not part of the serialized state, so every browser starts false even though the server render already fetched
  let hasFetchedPromos = false
  // Child entries name the parent whose card /deals lists them on, the same parent a category card or carousel renders
  const childPromoIdsByParent = computed(() => {
    const index: { [parentId: number]: number[] } = {}
    Object.values(activePromos.value).forEach(promo => {
      if (promo.dealsParentId) (index[promo.dealsParentId] ??= []).push(promo.productId)
    })

    return index
  })

  const customerStore = useCustomerStore()
  const listStore = useListStore()
  const productStore = useProductStore()

  function setCustomerPrices(values: PromoState['customerPrices'], rogue: IRogue) {
    if (!deepEqual(values, customerPrices.value)) {
      const updatedIds: number[] = []

      /**
       * Find all differences between the new customer price object and the old
       */
      Object.keys(customerPrices.value)
        .concat(Object.keys(values))
        .map(id => parseInt(id, 10))
        .forEach(id => {
          if (!updatedIds.includes(id) && values[id] && values[id] !== customerPrices.value[id]) {
            customerPrices.value[id] = values[id]
            updatedIds.push(id)
          }
        })

      if (updatedIds.length) {
        listStore.handleUpdatedPromos(rogue, updatedIds)
        updatedIds.forEach(productId => updateWithPromoPricing(rogue, productStore.products[productId]))
      }
    }
  }

  function setPromos(deals: HotDeal[], rogue: IRogue) {
    /**
     * UpdatedProducts will track all productIds that are actually changed.
     * activeProductIds will track only the newly active product ids.
     *
     * Once we update the state with the newly active deals, we can remove the ones that are no longer active.
     */
    const updatedProductIds: number[] = []
    const activeProductIds: number[] = []

    deals.forEach(deal => {
      activeProductIds.push(deal.productId)
      // Add the promo
      if (!deepEqual(activePromos.value[deal.productId], deal)) {
        updatedProductIds.push(deal.productId)

        activePromos.value[deal.productId] = deal
      }
    })

    // Remove nonexisting promos that may have existed locally beforehand
    Object.keys(activePromos.value)
      .map(id => parseInt(id, 10))
      .filter(id => !activeProductIds.includes(id))
      .forEach(id => {
        // Listing an already missed deal again would re-send the cart and rebuild the deals page on every poll
        if (hasFetchedPromos && activePromos.value[id].status === 'missed') return

        updatedProductIds.push(id)
        // The page's snapshot can be weeks old on the CDN, so a deal this browser never saw live is dropped, not missed
        if (hasFetchedPromos) activePromos.value[id].status = 'missed'
        else delete activePromos.value[id]
      })

    hasFetchedPromos = true

    // Only dispatch the event if anything has changed.
    if (updatedProductIds.length) {
      listStore.handleUpdatedPromos(rogue, updatedProductIds)
      updatedProductIds.forEach(productId => updateWithPromoPricing(rogue, productStore.products[productId]))
      lastUpdate.value = Date.now()
    }
  }

  function setHundoProducts(hundoIds: number[]) {
    hundoProductIds.value = hundoIds.map(integer => (`${integer}`))
  }

  async function reloadCustomerPricing(rogue: IRogue) {
    // "No Tier" is a real value, not an absent one - it is what most tiered-system customers carry.
    const tier = (customerStore.tier || '').trim().toLowerCase()
    const hasTier = !!tier && tier !== 'no tier'

    if ((!customerStore.groupId || customerStore.groupId < 2) && !hasTier) {
      // Guests (group 0) and normal customers (group 1) with no tier have no special prices
      setCustomerPrices({}, rogue)
    } else {
      try {
        const prices = await rogue.fetch<PromoState['customerPrices']>('/svc_content/catalog/product/group-prices')

        setCustomerPrices(prices, rogue)
      } catch (e) {
        rogue.log.debug('Error loading customer group prices', e)
      }
    }
  }

  /**
   * Reload the promos from the server
   * @todo Eventually, these updates will come from something like a websocket (svc_hotdeals) rather than an HTTP poll...
   */
  async function refresh(rogue: IRogue) {
    rogue.log.debug('Refreshing hotdeals')
    try {
      const data = await rogue.api.fetch<{ products: HotDeal[], hundoProductIds: number[] }>({
        url   : '/svc_content/hot/active',
        cache : { key: 'hotdeals', seconds: 60 },
      })
      setPromos(data.products, rogue)
      setHundoProducts(data.hundoProductIds)
    } catch (e) {
      rogue.log.debug('Error loading promo data', e)
    }
  }

  return {
    activePromos,
    childPromoIdsByParent,
    hundoProductIds,
    customerPrices,
    lastUpdate,
    reloadCustomerPricing,
    refresh,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(usePromoStore, import.meta.hot))
}